<script setup>
import Layout from "../../../layouts/main.vue";
import { onMounted, ref, reactive, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FormInstance } from "element-plus";
import MethodService from "../../../service/MethodService";
import DataForm from "./dataFormDynamicConfigTable";
import mushroom from "cem-primary-api";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
import IconUploadSelector from "../../../components/IconUploadSelector.vue";
const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const ruleFormRef = ref(FormInstance);
const loadingBtn = ref(false);

const isUpdate = ref(false);
const dataForm = reactive(MethodService.copyObject(DataForm.dataForm));
const typeOptions = DataForm.typeOptions;
const textAlignOptions = computed(() => [
  { value: "left", label: t("align_left") },
  { value: "center", label: t("align_center") },
  { value: "right", label: t("align_right") },
]);

// Validation rules cho form chính
const rules = reactive({
  name: [{ required: true, message: () => t("t-dynamic-config-table-validate-name-required"), trigger: "blur" }],
  "sql.select": [{ required: true, message: () => t("t-dynamic-config-table-validate-sql-select-required"), trigger: "blur" }],
  "sql.update": [{ required: true, message: () => t("t-dynamic-config-table-validate-sql-update-required"), trigger: "blur" }],
  "id_column.code": [{ required: true, message: () => t("t-dynamic-config-table-validate-id-column-required"), trigger: "blur" }],
  "id_column.type": [{ required: true, message: () => t("t-dynamic-config-table-validate-id-column-type-required"), trigger: "change" }],
});

// Dynamic rules cho value_columns items
const valueColumnCodeRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-value-columns-code"), trigger: "blur" }];
const valueColumnTypeRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-value-columns-type"), trigger: "change" }];
const valueColumnTypeCodeRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-value-columns-type-code"), trigger: "blur" }];
const valueColumnNameRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-value-columns-name"), trigger: "blur" }];

// Dynamic rules cho columns items
const columnCodeRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-columns-code"), trigger: "blur" }];
const columnLabelRules = [{ required: true, message: () => t("t-dynamic-config-table-validate-columns-label"), trigger: "blur" }];

// Width validator
const widthValidator = (rule, value, callback) => {
  if (value !== null && value !== undefined && value <= 0) {
    callback(new Error(t("t-dynamic-config-table-validate-width")));
  } else {
    callback();
  }
};
const columnWidthRules = [{ validator: widthValidator, trigger: "change" }];

// Thêm 1 column
const fn_addColumn = () => {
  dataForm.columns.push(MethodService.copyObject(DataForm.columnTemplate));
};

// Xóa 1 column
const fn_removeColumn = (index) => {
  dataForm.columns.splice(index, 1);
};

// Di chuyển column lên
const fn_moveColumnUp = (index) => {
  if (index <= 0) return;
  const temp = dataForm.columns[index];
  dataForm.columns.splice(index, 1);
  dataForm.columns.splice(index - 1, 0, temp);
};

// Di chuyển column xuống
const fn_moveColumnDown = (index) => {
  if (index >= dataForm.columns.length - 1) return;
  const temp = dataForm.columns[index];
  dataForm.columns.splice(index, 1);
  dataForm.columns.splice(index + 1, 0, temp);
};

// Thêm 1 value_column
const fn_addValueColumn = () => {
  dataForm.value_columns.push(
    MethodService.copyObject(DataForm.valueColumnTemplate),
  );
};

// Xóa 1 value_column
const fn_removeValueColumn = (index) => {
  dataForm.value_columns.splice(index, 1);
};

// Validate SQL update chứa @id@ và @XXX@ (XXX = value_columns.code)
const validateSqlUpdate = () => {
  const sqlUpdate = dataForm.sql.update;
  if (!sqlUpdate) return true;
  const sqlTrimmed = sqlUpdate.trim();

  if (!sqlTrimmed.includes("@id@")) {
    toastr.error(t("t-dynamic-config-table-validate-sql-update-id"));
    return false;
  }

  for (const col of dataForm.value_columns) {
    if (col.code && !sqlTrimmed.includes(`@${col.code.trim()}@`)) {
      toastr.error(
        t("t-dynamic-config-table-validate-sql-update-value").replace("{code}", col.code),
      );
      return false;
    }
  }

  return true;
};

// Validate value_columns length
const validateValueColumnsLength = () => {
  if (dataForm.value_columns.length < 1) {
    toastr.error(t("t-dynamic-config-table-validate-value-columns-min"));
    return false;
  }
  return true;
};

// Validate columns length
const validateColumnsLength = () => {
  if (dataForm.columns.length < 1) {
    toastr.error(t("t-dynamic-config-table-validate-columns-min"));
    return false;
  }
  return true;
};

// Thêm mới
const fn_ThemMoi = (formEl) => {
  loadingBtn.value = true;
  if (!formEl) return;

  formEl.validate(async (valid) => {
    if (valid) {
      if (!validateValueColumnsLength()) { loadingBtn.value = false; return; }
      if (!validateColumnsLength()) { loadingBtn.value = false; return; }
      if (!validateSqlUpdate()) { loadingBtn.value = false; return; }

      const payload = buildObjectDataForAPI();
      try {
        const res = await mushroom.dynamic_config_table.createAsync(payload);
        if (res.result) {
          toastr.success(t("t-dynamic-config-table-add-success"));
          router.push({ name: "DanhSachDynamicConfigTable" });
        }
      } catch (e) {
        MethodService.showError(e.code);
      } finally {
        loadingBtn.value = false;
      }
    } else {
      loadingBtn.value = false;
      return false;
    }
  });
};

// Cập nhật
const fn_CapNhat = (formEl) => {
  loadingBtn.value = true;
  if (!formEl) return;

  formEl.validate(async (valid) => {
    if (valid) {
      if (!validateValueColumnsLength()) { loadingBtn.value = false; return; }
      if (!validateColumnsLength()) { loadingBtn.value = false; return; }
      if (!validateSqlUpdate()) { loadingBtn.value = false; return; }

      const payload = buildObjectDataForAPI();
      payload.id = route.params.id;
      try {
        const res = await mushroom.dynamic_config_table.partialUpdateAsync(payload);
        if (res.result) {
          toastr.success(t("t-dynamic-config-table-update-success"));
          router.push({ name: "DanhSachDynamicConfigTable" });
        }
      } catch (e) {
        MethodService.showError(e.code);
      } finally {
        loadingBtn.value = false;
      }
    } else {
      loadingBtn.value = false;
      return false;
    }
  });
};

const hasSqlMode = computed(() => {
  return dataForm.value_columns && dataForm.value_columns.some((col) => col.type_mode === "sql");
});

const buildObjectDataForAPI = () => {
  return {
    name: dataForm.name,
    name_lang: dataForm.name_lang,
    icon: dataForm.icon || null,
    ui: {
      allow_paging: dataForm.ui.allow_paging,
    },
    sql: {
      select: dataForm.sql.select,
      update: dataForm.sql.update,
    },
    columns: dataForm.columns.map((col) => ({
      code: col.code,
      label: col.label,
      label_lang: col.label_lang,
      text_align: col.text_align || "left",
      width: col.width ? Number(col.width) : null,
      allow_search: col.allow_search || false,
      allow_sort: col.allow_sort || false,
      tooltip: col.tooltip || undefined,
    })),
    id_column: {
      code: dataForm.id_column.code,
      type: dataForm.id_column.type,
    },
    value_columns: dataForm.value_columns.map((col) => ({
      code: col.code,
      default_value_code: col.default_value_code || undefined,
      type_mode: col.type_mode || "static",
      type: col.type_mode === "sql" ? undefined : col.type,
      type_code: col.type_mode === "sql" ? col.type_code : undefined,
      name: col.name,
      name_lang: col.name_lang,
      min_code: col.min_code || undefined,
      max_code: col.max_code || undefined,
    })),
  };
};

// Lấy dữ liệu khi cập nhật
const fn_getDataDynamicConfigTableByID = async () => {
  try {
    const res = await mushroom.dynamic_config_table.findByIdAsync({
      id: route.params.id,
    });
    if (res.result) {
      const data = res.result;
      dataForm.name = data.name;
      dataForm.name_lang = data.name_lang || { vi: "", en: "" };
      dataForm.icon = data.icon;
      dataForm.ui = data.ui || { allow_paging: false };
      dataForm.sql = data.sql || { select: "", update: "" };
      dataForm.columns = data.columns || [];
      dataForm.id_column = data.id_column || { code: "", type: "string" };
      dataForm.value_columns = (data.value_columns || []).map((col) => ({
        code: col.code,
        default_value_code: col.default_value_code || "",
        type_mode: col.type_mode || "static",
        type: col.type || "string",
        type_code: col.type_code || "",
        name: col.name || "",
        name_lang: col.name_lang || { vi: "", en: "" },
        min_code: col.min_code || "",
        max_code: col.max_code || "",
      }));
    }
  } catch (e) {
    MethodService.showError(e.code);
  }
};

// Quay lại
const fn_QuayLai = () => {
  router.push({ name: "DanhSachDynamicConfigTable" });
};

onMounted(async () => {
  if (route.name === "CapNhatDynamicConfigTable") {
    isUpdate.value = true;
    await fn_getDataDynamicConfigTableByID();
  }
});
</script>

<template>
  <Layout>
    <el-form
      ref="ruleFormRef"
      :model="dataForm"
      :rules="rules"
      label-position="top"
      @submit.prevent
    >
      <!-- Card: Thông tin cơ bản -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0 d-flex align-items-center"><i class="ri-information-line me-1"></i>{{ $t("t-dynamic-config-table-basic-info") }}</h5>
        </div>
        <div class="card-body">
          <b-row>
            <b-col md="6">
              <el-form-item prop="name">
                <template #label>{{ $t('t-dynamic-config-table-name') }}</template>
                <el-input v-model="dataForm.name" :placeholder="$t('t-dynamic-config-table-name-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
            <b-col md="3">
              <el-form-item :label="$t('t-dynamic-config-table-name-vi')">
                <el-input v-model="dataForm.name_lang.vi" :placeholder="$t('t-dynamic-config-table-name-vi-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
            <b-col md="3">
              <el-form-item :label="$t('t-dynamic-config-table-name-en')">
                <el-input v-model="dataForm.name_lang.en" :placeholder="$t('t-dynamic-config-table-name-en-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <el-form-item label="Icon">
                <div class="d-flex align-items-center w-100">
                  <IconUploadSelector v-model="dataForm.icon" />
                  <el-tooltip effect="dark" :content="$t('t-dynamic-config-table-delete-icon')" placement="top">
                    <button
                      v-if="dataForm.icon"
                      type="button"
                      class="btn btn-soft-danger btn-sm ms-2"
                      style="height: 30px; display: flex; align-items: center;"
                      @click="dataForm.icon = null"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </el-tooltip>
                  <div class="ms-auto">
                    <el-checkbox v-model="dataForm.ui.allow_paging">{{ $t("t-dynamic-config-table-allow-paging") }}</el-checkbox>
                  </div>
                </div>
              </el-form-item>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Card: Câu SQL sinh dữ liệu -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0 d-flex align-items-center"><i class="ri-database-2-line me-1"></i>{{ $t("t-dynamic-config-table-sql-select") }}</h5>
        </div>
        <div class="card-body">
          <b-row>
            <b-col md="12">
              <el-form-item prop="sql.select">
                <el-input type="textarea" v-model="dataForm.sql.select" :placeholder="$t('t-dynamic-config-table-sql-select-placeholder')" :rows="4"></el-input>
              </el-form-item>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Card: Bảng cấu hình các cột hiển thị -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="card-title mb-0 d-flex align-items-center"><i class="ri-table-line me-1"></i>{{ $t("t-dynamic-config-table-columns") }}</h5>
          <button type="button" class="btn btn-soft-success btn-sm" @click="fn_addColumn">
            <i class="ri-add-line"></i> {{ $t("t-dynamic-config-table-add-btn") }}
          </button>
        </div>
        <div class="card-body p-0">
          <el-table :data="dataForm.columns" border style="width: 100%" size="small">
            <el-table-column :label="$t('t-stt')" type="index" width="60" align="center" />
            <el-table-column :label="$t('t-dynamic-config-table-code')" min-width="180" align="center">
              <template #default="scope">
                <el-form-item :prop="'columns.' + scope.$index + '.code'" :rules="columnCodeRules" style="margin-bottom: 0">
                  <el-input v-model="scope.row.code" :placeholder="$t('t-dynamic-config-table-code-placeholder')" size="small"></el-input>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-label')" min-width="130" align="center">
              <template #default="scope">
                <el-form-item :prop="'columns.' + scope.$index + '.label'" :rules="columnLabelRules" style="margin-bottom: 0">
                  <el-input v-model="scope.row.label" :placeholder="$t('t-dynamic-config-table-label-placeholder')" size="small"></el-input>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-label-vi')" min-width="120" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.label_lang.vi" :placeholder="$t('t-dynamic-config-table-label-vi-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-label-en')" min-width="120" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.label_lang.en" :placeholder="$t('t-dynamic-config-table-label-en-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-text-align')" width="130" align="center">
              <template #default="scope">
                <el-select v-model="scope.row.text_align" size="small" style="width: 100%">
                  <el-option v-for="item in textAlignOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-width')" width="110" align="center">
              <template #default="scope">
                <el-form-item :prop="'columns.' + scope.$index + '.width'" :rules="columnWidthRules" style="margin-bottom: 0">
                  <el-input-number v-model="scope.row.width" :controls="false" :min="1" size="small" style="width: 100%"></el-input-number>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-allow-search')" width="90" align="center">
              <template #default="scope">
                <el-checkbox v-model="scope.row.allow_search"></el-checkbox>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-allow-sort')" width="90" align="center">
              <template #default="scope">
                <el-checkbox v-model="scope.row.allow_sort"></el-checkbox>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-description')" min-width="150" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.tooltip" :placeholder="$t('t-dynamic-config-table-description-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-action')" width="120" align="center" fixed="right">
              <template #default="scope">
                <div class="d-flex justify-content-center gap-1">
                  <button type="button" class="btn btn-soft-secondary btn-sm" @click="fn_moveColumnUp(scope.$index)" :disabled="scope.$index === 0">
                    <i class="ri-arrow-up-line"></i>
                  </button>
                  <button type="button" class="btn btn-soft-secondary btn-sm" @click="fn_moveColumnDown(scope.$index)" :disabled="scope.$index === dataForm.columns.length - 1">
                    <i class="ri-arrow-down-line"></i>
                  </button>
                  <button type="button" class="btn btn-soft-danger btn-sm" @click="fn_removeColumn(scope.$index)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Card: Cấu hình cập nhật dữ liệu -->
      <div class="card">
        <div class="card-header">
          <h5 class="card-title mb-0 d-flex align-items-center"><i class="ri-edit-box-line me-1"></i>{{ $t("t-dynamic-config-table-update-config") }}</h5>
        </div>
        <div class="card-body">
          <b-row>
            <b-col md="12">
              <el-form-item prop="sql.update">
                <template #label>
                  {{ $t('t-dynamic-config-table-sql-update-label') }}
                  <el-tooltip effect="dark" :content="$t('t-dynamic-config-table-sql-update-tooltip')" placement="top">
                    <i class="ri-information-fill text-info ms-1" style="cursor: pointer; font-size: 14px;"></i>
                  </el-tooltip>
                </template>
                <el-input type="textarea" v-model="dataForm.sql.update" :placeholder="$t('t-dynamic-config-table-sql-update-placeholder')" :rows="4" style="width: 100%"></el-input>
              </el-form-item>
            </b-col>
          </b-row>
          <!-- ID Column -->
          <b-row>
            <b-col md="4">
              <el-form-item prop="id_column.code">
                <template #label>{{ $t('t-dynamic-config-table-identifier-code') }}</template>
                <el-input v-model="dataForm.id_column.code" :placeholder="$t('t-dynamic-config-table-code-placeholder')" size="large"></el-input>
              </el-form-item>
            </b-col>
            <b-col md="4">
              <el-form-item prop="id_column.type">
                <template #label>{{ $t('t-dynamic-config-table-data-type') }}</template>
                <el-select v-model="dataForm.id_column.type" size="large" style="width: 100%">
                  <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
                </el-select>
              </el-form-item>
            </b-col>
          </b-row>
          <!-- Value Columns -->
          <div class="d-flex justify-content-between align-items-center mb-2 mt-3">
            <h6 class="mb-0 d-flex align-items-center">
              {{ $t("t-dynamic-config-table-value-columns") }}
              <el-tooltip effect="dark" :content="$t('t-dynamic-config-table-validate-value-columns-min')" placement="top">
                <i class="ri-information-fill text-info ms-1" style="cursor: pointer; font-size: 14px;"></i>
              </el-tooltip>
            </h6>
            <button type="button" class="btn btn-soft-success btn-sm" @click="fn_addValueColumn">
              <i class="ri-add-line"></i> {{ $t("t-dynamic-config-table-add-btn") }}
            </button>
          </div>
          <el-table :data="dataForm.value_columns" border style="width: 100%" size="small">
            <el-table-column :label="$t('t-stt')" type="index" width="60" align="center" />
            <el-table-column :label="$t('t-dynamic-config-table-code')" min-width="180" align="center">
              <template #default="scope">
                <el-form-item :prop="'value_columns.' + scope.$index + '.code'" :rules="valueColumnCodeRules" style="margin-bottom: 0">
                  <el-input v-model="scope.row.code" :placeholder="$t('t-dynamic-config-table-code-placeholder')" size="small"></el-input>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-default-value-code')" min-width="130" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.default_value_code" :placeholder="$t('t-dynamic-config-table-default-value-code-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="Type mode" width="100" align="center">
              <template #default="scope">
                <el-checkbox v-model="scope.row.type_mode" true-label="sql" false-label="static"></el-checkbox>
              </template>
            </el-table-column>
            <el-table-column width="180" align="center">
              <template #header>
                <span>{{ hasSqlMode ? $t('t-dynamic-config-table-data-type-column') : $t('t-dynamic-config-table-data-type') }}</span>
              </template>
              <template #default="scope">
                <el-form-item v-if="scope.row.type_mode === 'sql'" :prop="'value_columns.' + scope.$index + '.type_code'" :rules="valueColumnTypeCodeRules" style="margin-bottom: 0">
                  <el-input v-model="scope.row.type_code" :placeholder="$t('t-dynamic-config-table-data-type-column-placeholder')" size="small"></el-input>
                </el-form-item>
                <el-form-item v-else :prop="'value_columns.' + scope.$index + '.type'" :rules="valueColumnTypeRules" style="margin-bottom: 0">
                  <el-select v-model="scope.row.type" size="small" style="width: 100%">
                    <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
                  </el-select>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-value-name')" min-width="120" align="center">
              <template #default="scope">
                <el-form-item :prop="'value_columns.' + scope.$index + '.name'" :rules="valueColumnNameRules" style="margin-bottom: 0">
                  <el-input v-model="scope.row.name" :placeholder="$t('t-dynamic-config-table-value-name-placeholder')" size="small"></el-input>
                </el-form-item>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-value-name-vi')" min-width="120" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.name_lang.vi" :placeholder="$t('t-dynamic-config-table-value-name-vi-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-value-name-en')" min-width="120" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.name_lang.en" :placeholder="$t('t-dynamic-config-table-value-name-en-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-min')" width="140" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.min_code" :placeholder="$t('t-dynamic-config-table-min-code-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-dynamic-config-table-max')" width="140" align="center">
              <template #default="scope">
                <el-input v-model="scope.row.max_code" :placeholder="$t('t-dynamic-config-table-max-code-placeholder')" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t-action')" width="100" align="center" fixed="right">
              <template #default="scope">
                <div style="display: flex; justify-content: center;">
                  <button type="button" class="btn btn-soft-danger btn-sm" @click="fn_removeValueColumn(scope.$index)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="dataForm.value_columns.length === 0" class="text-muted text-center py-3">
            {{ $t("t-dynamic-config-table-no-config") }}
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="button-group">
        <button type="button" class="btn btn-soft-secondary btn-border me-2" @click="fn_QuayLai">
          {{ $t("t-back") }}
        </button>
        <button v-if="!isUpdate" type="button" class="btn btn-secondary btn-border" :disabled="loadingBtn" @click="fn_ThemMoi(ruleFormRef)">
          <i class="ri-save-line me-1"></i> {{ $t("t-add") }}
        </button>
        <button v-else type="button" class="btn btn-secondary btn-border" :disabled="loadingBtn" @click="fn_CapNhat(ruleFormRef)">
          <i class="ri-save-line me-1"></i> {{ $t("t-update") }}
        </button>
      </div>
    </el-form>
  </Layout>
</template>

<style scoped lang="scss">
.button-group {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}
.btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
